import React, { useRef, useState, useEffect } from "react";
import axios from "axios";
import "./style.scss";
import EmailIcon from "../../../assets/icons/email.svg";
import LockIcon from "../../../assets/icons/lock.svg";
import EyeIcon from "../../../assets/icons/eye.svg";
import EyeSlashedIcon from "../../../assets/icons/eye-slashed.svg";
import CheckSolidGreenIcon from "../../../assets/icons/check-solid-green.svg";
import ArrowRightIcon from "../../../assets/icons/arrow-right.svg";
import GoogleIcon from "../../../assets/icons/googleIcon.svg";
import FacebookIcon from "../../../assets/icons/facebook.svg";
import DiscordIcon from "../../../assets/icons/discord-clean.svg";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { validateEmail, validatePassword } from "../../../utils/helpers/string";
import Tooltip from "../../../reusableComponents/Tooltip";
import { useNavigate } from "react-router-dom";
import Spinner from "../../../reusableComponents/Spinner";
import { clearPersistedData } from "../../../store/configureStore";
import { loginAPI } from "../../../utils/apis/auth";
import { setAuthenticationFailure, setAuthenticationSuccess, setIsProfilePopupVisible } from "../../../store/reducers/authSlice";
import { getUser } from "../../../store/reducers/userSlice";
import { Checkbox, FormControlLabel } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import CompanyLogo from "../../../assets/icons/equityEdgeLogo.svg";
import { returnErrors } from "../../../store/reducers/error";
import Loader from "../../../reusableComponents/Loader";
import ForgotPasswordForm from "../ForgotPasswordForm";

function SigninForm() {
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleAuthLoading, setIsGoogleAuthLoading] = useState(false);

  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const selectedBrand = useSelector((state) => state.brand.selectedBrand);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  useEffect(() => {
    if (isAuthenticated) {
      if (window.location.pathname === "/signup") {
        navigate("/funding-evaluation/plans/0");
      } else {
        navigate("/");
      }
    }
  }, [isAuthenticated]);

  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  let emailRef = useRef(null);
  let passwordRef = useRef(null);

  // Function to show the profile modal
  function checkProfileDetailsAndShowPopup(userData) {
    console.log(userData, "userData");
    // Will match empty string, undefined, null for both fields
    if (!userData.contact || !userData.country) {
      dispatch(setIsProfilePopupVisible({ isVisible: true, contact: !userData.contact, country: !userData.country }));
      setTimeout(() => {
        dispatch(setIsProfilePopupVisible({ isVisible: false }));
      }, 15000);
    }
  }

  //3rd party Auth
  const handleGoogleAuth = async (e) => {
    e.preventDefault();
    setIsGoogleAuthLoading(true);
    // Set loading state to true when Google Auth starts

    // Create a promise to await the completion of the redirection
    const googleAuthPromise = new Promise((resolve) => {
      let path =
        "https://accounts.google.com/o/oauth2/v2/auth?scope=https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/user.phonenumbers.read https://www.googleapis.com/auth/user.addresses.read&include_granted_scopes=true&response_type=code&state=pass-through value&redirect_uri=https://app.equityedge.io/auth/google&client_id=454348839117-icvli9lq10npbbautre2h62keggrvnlp.apps.googleusercontent.com";
      // Redirect to Google authentication URL
      window.location.replace(path);

      // Resolve the promise after redirection
      resolve();
    }).then((res) => {
      setIsGoogleAuthLoading(false);
    });

    // Await the completion of the redirection
    await googleAuthPromise;

    // Set loading state to false when Google Auth finishes
  };

  const schema = Yup.object().shape({
    email: Yup.string()
      .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, "Invalid email format")
      .required("Email is required"),
    password: Yup.string().required("Password is required"),
    // .min(8, "Password must be at least 8 characters long")
    // .matches(/^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/, "Password must contain at least one uppercase letter, one number, one special character, and be at least 8 characters long")
    // .max(16, "Password must not exceed 16 characters"),
  });

  const inputs = {
    email: "",
    password: "",
    rememberMe: false,
  };

  const { values, errors, touched, handleChange, handleBlur, handleSubmit } = useFormik({
    initialValues: inputs,
    validationSchema: schema,
    onSubmit: async (values) => {
      if (errors.email) {
        return;
      }

      const formData = new FormData();
      Object.keys(values).forEach((key) => {
        formData.append(key, values[key]);
      });
      // const response = await signupRequest(formData);
      try {
        setIsLoading(true);
        const handleLoginRes = await loginAPI(values);

        setIsLoading(false);
        if (handleLoginRes.status === 200) {
          const userData = handleLoginRes.data;
          checkProfileDetailsAndShowPopup(userData);

          if (userData.sessionToken) {
            localStorage.setItem("ee-session-token", userData.sessionToken);
            axios.defaults.headers.common["X-SESSION-TOKEN"] = userData.sessionToken;
          }

          dispatch(
            setAuthenticationSuccess({
              ...handleLoginRes,
              rememberMe: values.rememberMe,
            }),
          );
          dispatch(getUser({ idToken: handleLoginRes.data.idToken, selectedBrand }));
        } else {
          dispatch(setAuthenticationFailure(handleLoginRes));
          let msg = handleLoginRes?.response?.data?.detail
            ? handleLoginRes?.response?.data?.detail
            : handleLoginRes?.response?.data?.non_field_errors[0]
              ? handleLoginRes.response.data.non_field_errors[0]
              : "An error occurred.";

          if (msg === "INVALID_PASSWORD") {
            msg = "Invalid username or password.\nPlease check your credentials and try again.";
          }

          dispatch(returnErrors(msg, handleLoginRes?.response?.status));
        }
      } catch (err) {
        dispatch(setAuthenticationFailure(err));

        let errorMsg = "An error occurred.";
        if (err.response && err.response.data) {
          errorMsg = err.response.data.detail || err.response.data.non_field_errors?.[0] || err.response.data.error || "Invalid email or password.";
        } else if (err.message) {
          errorMsg = err.message;
        }

        if (errorMsg === "INVALID_PASSWORD") {
          errorMsg = "Invalid username or password.\nPlease check your credentials and try again.";
        }

        dispatch(returnErrors(errorMsg, err.response?.status || 500));
      }
      setTimeout(() => {
        setIsLoading(false);
      }, 2000);
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (isGoogleAuthLoading) {
    return <Loader />;
  }

  return (
    <div className="signinform-container">
      <div className="signinform_wrapper">
        <form className="signinform-innercontainer signinform-container_form">
          <div className="signinform-subcontainer_form">
            <div className="inputTitle">
              <p>Email</p>
            </div>
            <label
              ref={emailRef}
              className="signinform-innercontainer_form"
            >
              <div className="formInputContainer emailWrapper">
                <input
                  style={{ color: "#000 !important" }}
                  type="text"
                  name="email"
                  id="email"
                  className="signinform-input inputDiv"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Type Your Email "
                />
                <div className="inputContainer emailVerify">
                  {validateEmail(values.email) && (
                    <img
                      src={CheckSolidGreenIcon}
                      alt=""
                      className="signinform-icon"
                    />
                  )}
                </div>
              </div>
            </label>
            <div style={{ display: "flex", color: "red", fontSize: "15px", marginTop: "5px" }}> {touched.email && errors.email && <div className="error-message">{errors.email}</div>}</div>
          </div>
          <div className="signinform-subcontainer_form">
            <div className="inputTitle">
              <p>Password</p>
            </div>
            <label
              ref={passwordRef}
              className="signinform-innercontainer_form"
            >
              <div className="formInputContainer passwordInputContainer">
                <input
                  type={isPasswordVisible ? "text" : "password"}
                  name="password"
                  id="password"
                  className="signinform-input inputDiv"
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Type Your Password "
                />
                <div className="inputContainer eyeIcon">
                  <img
                    src={isPasswordVisible ? EyeIcon : EyeSlashedIcon}
                    alt=""
                    className="signinform-icon"
                    onClick={() => setIsPasswordVisible((prev) => !prev)}
                  />
                </div>
              </div>
            </label>
            <div style={{ display: "flex", color: "red", fontSize: "15px", marginTop: "5px" }}> {touched.password && errors.password && <div className="error-message">{errors.password}</div>}</div>
          </div>

          <div className="signinform-subcontainer_form signinform-container_remember-me">
            <div className="signinform_subcontainer_wrapper">
              {/* <input
                type="checkbox"
                name="checkbox"
                id="checkbox"
                style={{backgroundColor: "red"}}
              />
              <label
                className="signinform-label signinform-label_checkbox"
                htmlFor="checkbox"
              >
                Remember me
              </label> */}
              <FormControlLabel
                className="rememberSection"
                control={
                  <Checkbox
                    name="rememberMe"
                    checked={values.rememberMe}
                    onChange={handleChange}
                  />
                }
                sx={{
                  "& .MuiSvgIcon-root": {
                    color: "#FFF", // Default color
                    borderRadius: "11px",
                    fontSize: "14px", // Set the size of the SVG icon
                  },
                  "&.Mui-checked": {
                    backgroundColor: "#158F91", // Background color when checked
                  },
                }}
                label="Remember Me"
              />
            </div>
            <div className="signinform_subcontainer_wrapper">
              <Link to="/forgot-password">
                <label
                  className="signinform-label forgot_password"
                  htmlFor="checkbox"
                >
                  Forgot Password ?
                </label>
              </Link>
            </div>
          </div>
          <div className="signinform-container_button">
            <button
              className="signinform-button"
              onClick={handleSubmit}
              disabled={isLoading}
            >
              {isLoading ? <Spinner /> : <>Submit</>}
            </button>
          </div>
          <div className="optionSection">
            <div className="line"></div>
            <div className="Option">
              <p>Or</p>
            </div>
            <div className="line"></div>
          </div>

          <div
            className="signinform-link"
            onClick={handleGoogleAuth}
          >
            {/* Don&#39;t have an account?{" "}
            <Link to="/signup">
              <b className="signinform-link_bold">Sign Up</b>
            </Link> */}
            <div className="icon">
              <img
                src={GoogleIcon}
                alt=""
              />
            </div>
            <div className="signup">
              <p>Sign in with Google</p>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default SigninForm;
